/**
 * Schicht 1: Pure functions for task duration calculation.
 * Migrated from pertCalculations.ts + simulation.ts init code.
 * All calculations in days (no days↔weeks back-and-forth).
 */

import type { Task, PertEstimate } from '../../types';
import { pertExpectedValue, pertStandardDeviation } from '../../types';
import { getTaskDayIndex } from '../../lib/dateUtils';
import type { ContributionDetail } from './types';

const DAYS_PER_WEEK = 5;
const TEAM_COLLABORATION_PENALTY = 0.3; // 30% loss per additional team member

// ============================================
// Types
// ============================================

export interface TaskBaseDuration {
  taskId: string;
  startDay: number;
  baseDays: number;           // Primary assignee alone (PERT expected days)
  effectiveDays: number;      // With team speed (before multitasking)
  endDay: number;             // startDay + effectiveDays - 1
  teamWeeks: number;          // effectiveDays / 5
  totalSpeed: number;         // Combined team speed
  totalRisk: number;          // Combined standard deviation
  bestCaseWeeks: number;      // With optimistic estimates
  contributionDetails: ContributionDetail[];
  inputHash: string;
}

// ============================================
// Hash for incremental cache
// ============================================

export function computeTaskInputHash(task: Task, boardStartDate: string): string {
  const parts = [
    boardStartDate,
    task.startDate,
    task.estimateMode,
    task.column,
    task.assigneeIds.join(','),
  ];

  if (task.defaultEstimate) {
    const d = task.defaultEstimate;
    parts.push(`d:${d.o}/${d.m}/${d.p}`);
  }

  for (const empId of task.assigneeIds) {
    const est = task.estimates[empId];
    if (est) {
      parts.push(`e:${empId}:${est.o}/${est.m}/${est.p}`);
    }
  }

  return parts.join('|');
}

// ============================================
// Effective estimate (from pertCalculations.ts)
// ============================================

export function getEffectiveEstimate(task: Task, employeeId: string): PertEstimate | null {
  if (task.estimateMode === 'default') {
    return task.defaultEstimate ?? null;
  }
  const individual = task.estimates[employeeId];
  return individual ?? null;
}

// ============================================
// Simple PERT duration for unassigned tasks (from pertCalculations.ts)
// ============================================

export function getTaskDurationFromPert(task: Task): number {
  if (task.estimateMode === 'default' && task.defaultEstimate) {
    const pert = task.defaultEstimate;
    return Math.ceil((pert.o + 4 * pert.m + pert.p) / 6);
  }

  const primaryId = task.assigneeIds[0];
  if (primaryId && task.estimates[primaryId]) {
    const pert = task.estimates[primaryId]!;
    return Math.ceil((pert.o + 4 * pert.m + pert.p) / 6);
  }

  if (task.defaultEstimate) {
    const pert = task.defaultEstimate;
    return Math.ceil((pert.o + 4 * pert.m + pert.p) / 6);
  }

  return 10; // Default duration
}

// ============================================
// Team contribution calculation (from pertCalculations.ts)
// ============================================

function calculateContributions(task: Task): {
  contributions: ContributionDetail[];
  totalSpeed: number;
  teamWeeks: number;
  totalRisk: number;
  bestCaseWeeks: number;
} {
  if (task.assigneeIds.length === 0) {
    return { contributions: [], totalSpeed: 0, teamWeeks: 0, totalRisk: 0, bestCaseWeeks: 0 };
  }

  const contributions: ContributionDetail[] = [];
  let totalSpeed = 0;
  let bestCaseTotalSpeed = 0;

  for (const empId of task.assigneeIds) {
    const pert = getEffectiveEstimate(task, empId);
    if (!pert) continue;
    const estimate = pertExpectedValue(pert);
    const risk = pertStandardDeviation(pert);
    const speed = 1 / estimate;
    totalSpeed += speed;
    const bestCaseSpeed = 1 / (pert.o / DAYS_PER_WEEK);
    bestCaseTotalSpeed += bestCaseSpeed;
    contributions.push({
      employeeId: empId,
      estimate,
      pert,
      risk,
      speed,
      contributionPercent: 0,
    });
  }

  for (const contribution of contributions) {
    contribution.contributionPercent = (contribution.speed / totalSpeed) * 100;
  }

  // Team collaboration penalty: exponential overhead for larger teams
  const teamSize = contributions.length;
  if (teamSize > 1) {
    const efficiency = Math.pow(1 - TEAM_COLLABORATION_PENALTY, teamSize - 1);
    totalSpeed *= efficiency;
    bestCaseTotalSpeed *= efficiency;
  }

  const teamWeeks = totalSpeed > 0 ? 1 / totalSpeed : 0;
  const bestCaseWeeks = bestCaseTotalSpeed > 0 ? 1 / bestCaseTotalSpeed : 0;

  let totalVariance = 0;
  for (const contribution of contributions) {
    const weight = contribution.contributionPercent / 100;
    totalVariance += Math.pow(contribution.risk * weight, 2);
  }
  const totalRisk = Math.sqrt(totalVariance);

  return { contributions, totalSpeed, teamWeeks, totalRisk, bestCaseWeeks };
}

// ============================================
// Single task base duration (Schicht 1)
// ============================================

export function computeTaskBaseDuration(task: Task, boardStartDate: string): TaskBaseDuration {
  const startDay = getTaskDayIndex(task.startDate, boardStartDate);
  const inputHash = computeTaskInputHash(task, boardStartDate);

  // Unassigned tasks: simple PERT duration
  if (task.assigneeIds.length === 0) {
    const duration = getTaskDurationFromPert(task);
    return {
      taskId: task.id,
      startDay,
      baseDays: duration,
      effectiveDays: duration,
      endDay: startDay + duration - 1,
      teamWeeks: 0,
      totalSpeed: 0,
      totalRisk: 0,
      bestCaseWeeks: duration / DAYS_PER_WEEK,
      contributionDetails: [],
      inputHash,
    };
  }

  // Assigned tasks: PERT + team speed
  const primaryAssigneeId = task.assigneeIds[0];
  const primaryPert = getEffectiveEstimate(task, primaryAssigneeId);

  if (!primaryPert) {
    // No estimate for primary assignee — fallback
    const duration = getTaskDurationFromPert(task);
    return {
      taskId: task.id,
      startDay,
      baseDays: duration,
      effectiveDays: duration,
      endDay: startDay + duration - 1,
      teamWeeks: 0,
      totalSpeed: 0,
      totalRisk: 0,
      bestCaseWeeks: duration / DAYS_PER_WEEK,
      contributionDetails: [],
      inputHash,
    };
  }

  const baseWeeks = pertExpectedValue(primaryPert);
  const baseDays = baseWeeks * DAYS_PER_WEEK;
  const { contributions, totalSpeed, teamWeeks, totalRisk, bestCaseWeeks } = calculateContributions(task);

  // effectiveDays = teamWeeks * DAYS_PER_WEEK (team speed applied)
  const effectiveDays = teamWeeks * DAYS_PER_WEEK;
  const endDay = startDay + Math.ceil(effectiveDays) - 1;

  return {
    taskId: task.id,
    startDay,
    baseDays,
    effectiveDays: Math.ceil(effectiveDays),
    endDay,
    teamWeeks,
    totalSpeed,
    totalRisk,
    bestCaseWeeks,
    contributionDetails: contributions,
    inputHash,
  };
}

// ============================================
// Batch computation
// ============================================

export function computeAllBaseDurations(
  tasks: Task[],
  boardStartDate: string,
): Map<string, TaskBaseDuration> {
  const result = new Map<string, TaskBaseDuration>();
  for (const task of tasks) {
    result.set(task.id, computeTaskBaseDuration(task, boardStartDate));
  }
  return result;
}
